<template>
  <section class="block mx-auto mt-32 max-w-448">
    <SitterAlertBanner v-if="serverError" :error="serverError" />
    <PoppinsCard>
      <CareTypeFormHeader careType="petcare" />
      <form id="create-profile-pet-care" novalidate @submit.prevent="handleSubmit">
        <div v-for="(field, index) in formFields" :key="index">
          <component :is="field.component" ref="childComponents" v-bind="field.props" />
        </div>
        <PoppinsButton
          id="formSubmit"
          :loading="loading"
          class="w-full my-8"
          variant="primary"
          value="Save & Continue"
          size="medium"
          type="submit"
        />
      </form>
    </PoppinsCard>
  </section>
</template>

<script lang="ts">
import { UPSERT_PROFILE_SITTER } from '@/graphql/profile/mutations';
import SitterAlertBanner from '@/apps/sitter/signup/account/components/SitterAlertBanner.vue';
import CareTypeFormHeader from '../components/CareTypeFormHeader.vue';
import MaxDistanceSelect from '../components/MaxDistanceSelect.vue';
import ExperienceCheckbox from '../components/ExperienceCheckbox.vue';
import QualificationsCheckbox from '../components/QualificationsCheckbox.vue';
import YearsExperienceField from '../components/YearsExperienceField.vue';
import MaxPetsSelect from '../components/MaxPetsSelect.vue';
import ServiceRates from '../components/ServiceRates.vue';
import BioArea from '../components/BioArea.vue';
import Button from '@/components/button/Button.vue';
import Card from '@/components/card/Card.vue';
import { petCareExperienceOptions } from '../components/__types__/ExperienceTypes';
import { petCareQualificationsOptions } from '../components/__types__/QualificationsTypes';
import { navigateToRoute } from '@/lib/sitterOnboarding/sitterOnboardingState';

export default {
  name: 'ProviderPetCareForm',
  components: {
    SitterAlertBanner,
    CareTypeFormHeader,
    MaxDistanceSelect,
    MaxPetsSelect,
    ServiceRates,
    YearsExperienceField,
    ExperienceCheckbox,
    QualificationsCheckbox,
    BioArea,
    PoppinsCard: Card,
    PoppinsButton: Button,
  },
  data(): any {
    return {
      formFields: [
        { component: 'YearsExperienceField' },
        {
          component: 'ExperienceCheckbox',
          props: {
            experienceTypes: petCareExperienceOptions,
            generateScreenReaderLabel: (optionText) => `I have paid experience with ${optionText}`,
            legend: 'What types of pets do you have experience caring for?',
          },
        },
        {
          component: 'QualificationsCheckbox',
          props: {
            options: petCareQualificationsOptions,
            generateScreenReaderLabel: (optionText) => `I have ${optionText} certification`,
            legend: 'Do you have any certifications?',
          },
        },
        { component: 'MaxDistanceSelect' },
        { component: 'MaxPetsSelect' },
        { component: 'ServiceRates' },
        { component: 'BioArea' },
      ],
      serverError: null,
      loading: false,
    };
  },
  computed: {
    firstInvalidComponent() {
      return this.$refs.childComponents.find((childComponent) => {
        return childComponent.validate && !childComponent.validate();
      });
    },
  },
  methods: {
    handleSubmit() {
      this.$refs.childComponents.forEach((childComponent) => {
        if (childComponent.validate) {
          childComponent.validate();
        }
      });

      return this.firstInvalidComponent ? this.sendFocusToError() : this.upsertProfile();
    },
    sendFocusToError() {
      this.firstInvalidComponent.focusElement?.();
    },
    async upsertProfile() {
      try {
        await this.$apollo.mutate({
          mutation: UPSERT_PROFILE_SITTER,
          variables: {
            profile: {
              petCare: {
                yearsExperience: Number(this.$store.getters.getFormField('yearsExperience')),
                experience: this.$store.getters.getFormField('experience'),
                // Other free form text isn't supported so this removes text values until this is supported.
                experienceOther: '',
                qualifications: this.$store.getters.getFormField('qualifications'),
                distanceWillingToTravel: Number(this.$store.getters.getFormField('distanceWillingToTravel')),
                maxNumberOfPets: Number(this.$store.getters.getFormField('maxNumberOfPets')),
                serviceRates: this.$store.getters.selectedServiceRates,
                bio: this.$store.getters.getFormField('bio'),
              },
            },
          },
        });
        console.log('profile created'); // eslint-disable-line no-console
        this.loading = true;
        navigateToRoute('/sitter/verify_identity', this.$router);
      } catch (error) {
        console.log(error); // eslint-disable-line no-console
        this.serverError = new Error('unknown-error');
      }
    },
  },
};
</script>
